// Registration scheduling gate.
//
// A registration can be made publicly VIEWABLE while registration itself is not
// yet open. This is opt-in via `registration.config.gateByStartDate` (a
// registration-level-only config flag, read directly off registration.config —
// NOT resolved through getConfig). When enabled, users cannot register until
// `registration.startDate` (a date+time) has passed.
//
// `registration.startDate` always has a value (the schema defaults it to "now"),
// so the boolean flag — not the mere presence of startDate — is what turns the
// gate on. Single source of truth shared by apps/admin, apps/app, and the API
// backend so all three agree on "is registration open?".

export interface RegistrationScheduleLike {
  startDate?: Date | string | number | null;
  endDate?: Date | string | number | null;
  config?: {
    gateByStartDate?: boolean | null;
    gateByEndDate?: boolean | null;
  } | null;
}

/** The configured open time, or null when gating is off / startDate is unusable. */
export function getRegistrationStart(
  registration: RegistrationScheduleLike | null | undefined,
): Date | null {
  if (!registration?.config?.gateByStartDate) return null;
  if (registration.startDate == null) return null;
  const d = new Date(registration.startDate as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * True when this registration is gated AND its start time is still in the
 * future — i.e. registration should be blocked. False when gating is off, the
 * start date is unusable, or the start time has passed.
 */
export function isBeforeRegistrationStart(
  registration: RegistrationScheduleLike | null | undefined,
  now: Date = new Date(),
): boolean {
  const start = getRegistrationStart(registration);
  if (!start) return false;
  return now.getTime() < start.getTime();
}

/** The configured close time, or null when the end gate is off / endDate is unusable. */
export function getRegistrationEnd(
  registration: RegistrationScheduleLike | null | undefined,
): Date | null {
  if (!registration?.config?.gateByEndDate) return null;
  if (registration.endDate == null) return null;
  const d = new Date(registration.endDate as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * True when this registration is gated by an end date AND that end time has
 * passed — i.e. new registrations should be blocked. False when the end gate is
 * off, the end date is unusable, or the close time is still in the future.
 * (Existing teams remain editable regardless — callers gate only NEW teams.)
 */
export function isAfterRegistrationEnd(
  registration: RegistrationScheduleLike | null | undefined,
  now: Date = new Date(),
): boolean {
  const end = getRegistrationEnd(registration);
  if (!end) return false;
  return now.getTime() >= end.getTime();
}

export default isBeforeRegistrationStart;
